(() => {
  'use strict';
  if (window.LimitlessServiceRotation) { window.LimitlessServiceRotation.init(document); return; }
  const cards = new Map();
  const motion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const cadence = 6000;
  const transition = 550;
  let pageActive = true;
  const now = () => window.performance.now();
  const includes = (root, node) => root === document || root === node || Boolean(root.contains?.(node));
  const sharedFor = section => [...document.querySelectorAll('[data-ll-shared-motion]')].find(node => node.dataset.llHeroMediaKey === section.dataset.llServiceRotation);
  const belongs = (root, state) => includes(root, state.section) || includes(root, state.card) || (state.sharedScope && includes(root, state.sharedScope));
  const canRun = state => Boolean(observer) && pageActive && !motion?.matches && !connection?.saveData && !document.hidden && state.visible && !state.userPaused && !state.focused && state.card.isConnected && !state.card.closest('[hidden], [inert], [aria-hidden="true"]');
  const sourceKey = value => {
    try { return new URL(value, document.baseURI).pathname.replace(/-w\d+(?=\.[^.]+$)/, ''); } catch (_) { return ''; }
  };
  const stopClock = state => {
    if (state.timer === null) return;
    window.clearTimeout(state.timer);
    state.timer = null;
    state.remaining = Math.max(0, state.remaining - (now() - state.started));
  };
  const startClock = state => {
    if (!canRun(state) || state.timer !== null || !state.callback) return;
    state.started = now();
    state.timer = window.setTimeout(() => {
      state.timer = null;
      state.remaining = 0;
      const callback = state.callback;
      if (canRun(state)) { state.callback = null; callback(); } else paint(state);
    }, state.remaining);
  };
  const schedule = (state, callback, delay) => {
    stopClock(state);
    state.callback = callback;
    state.remaining = delay;
    startClock(state);
  };
  const cancelPending = state => {
    if (!state.pending) return;
    state.pending.image.onload = null;
    state.pending.image.onerror = null;
    state.pending = null;
    delete state.card.dataset.llServicePendingProject;
    state.generation += 1;
    stopClock(state);
    state.callback = () => rotate(state);
    state.remaining = cadence;
  };
  const finishTransition = state => {
    stopClock(state);
    state.animation?.cancel();
    state.animation = null;
    state.previous?.remove();
    state.previous = null;
    delete state.card.dataset.llServicePreviousProject;
    state.image.style.removeProperty('opacity');
    state.image.removeAttribute('data-ll-service-incoming');
    schedule(state, () => rotate(state), cadence);
  };
  const paint = state => {
    if (!cards.has(state.card)) return;
    const running = canRun(state);
    state.card.dataset.llServiceState = running && Boolean(state.callback || state.pending) ? 'running' : 'paused';
    if (running) { state.animation?.play(); startClock(state); }
    else { stopClock(state); state.animation?.pause(); cancelPending(state); }
    if ((motion?.matches || connection?.saveData) && state.previous) finishTransition(state);
  };
  const shuffle = entries => {
    const result = entries.slice();
    for (let i = result.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
  };
  const nextEntry = state => {
    const pool = window.LimitlessHomeProjects ? window.LimitlessHomeProjects.servicePool(state.pool, state.card) : state.pool;
    const allowed = new Set(pool.map(entry => entry.id));
    state.bag = state.bag.filter(entry => allowed.has(entry.id) && !state.failed.has(entry.id) && entry.id !== state.currentId);
    if (!state.bag.length) state.bag = shuffle(pool.filter(entry => !state.failed.has(entry.id) && entry.id !== state.currentId));
    return state.bag.shift();
  };
  const show = (state, pending) => {
    if (state.pending !== pending || pending.generation !== state.generation || !cards.has(state.card) || !canRun(state)) return;
    stopClock(state);
    if (window.LimitlessHomeProjects && !window.LimitlessHomeProjects.servicePool([pending.entry], state.card).length) { cancelPending(state); schedule(state, () => rotate(state), cadence); return; }
    state.pending = null;
    delete state.card.dataset.llServicePendingProject;
    state.card.dataset.llServicePreviousProject = state.currentId || '';
    pending.image.onload = null;
    pending.image.onerror = null;
    state.previous = state.image;
    state.previous.alt = '';
    state.previous.setAttribute('aria-hidden', 'true');
    state.previous.removeAttribute('data-ll-service-current');
    state.image = pending.image;
    state.currentId = pending.entry.id;
    state.image.alt = pending.entry.alt;
    state.image.setAttribute('data-ll-service-current', '');
    state.image.setAttribute('data-ll-service-incoming', '');
    state.card.append(state.image);
    state.card.dataset.llServiceProject = pending.entry.id;
    window.LimitlessHomeProjects?.rememberService(state.selectionKey, pending.entry.id);
    try {
      state.animation = state.image.animate?.([{opacity: 0}, {opacity: 1}], {duration: transition, easing: 'ease-in-out', fill: 'both'}) || null;
    } catch (_) { state.animation = null; }
    if (state.animation) schedule(state, () => finishTransition(state), transition);
    else finishTransition(state);
  };
  const rotate = state => {
    if (!cards.has(state.card) || !canRun(state) || state.pending || state.previous) return;
    const entry = nextEntry(state);
    if (!entry) { state.callback = null; paint(state); return; }
    const image = new Image();
    const pending = {image, entry, generation: ++state.generation, decoding: false};
    state.pending = pending;
    state.card.dataset.llServicePendingProject = entry.id;
    image.className = state.image.className;
    image.width = entry.width;
    image.height = entry.height;
    image.alt = entry.alt;
    image.decoding = 'async';
    image.loading = 'eager';
    image.fetchPriority = 'low';
    image.sizes = state.image.sizes || '(min-width: 750px) 42vw, 90vw';
    image.style.objectFit = entry.fit;
    image.style.objectPosition = entry.position;
    const failed = () => {
      if (state.pending !== pending || !cards.has(state.card)) return;
      stopClock(state);
      image.onload = null;
      image.onerror = null;
      state.pending = null;
      delete state.card.dataset.llServicePendingProject;
      state.failed.add(entry.id);
      state.callback = null;
      if (canRun(state)) rotate(state); else schedule(state, () => rotate(state), cadence);
    };
    const ready = () => {
      if (state.pending !== pending || pending.decoding) return;
      if (!image.naturalWidth) { failed(); return; }
      pending.decoding = true;
      if (typeof image.decode === 'function') {
        image.decode().then(() => show(state, pending)).catch(failed);
      } else show(state, pending);
    };
    image.onload = ready;
    image.onerror = failed;
    schedule(state, failed, 10000);
    if (entry.srcset) image.srcset = entry.srcset;
    image.src = entry.src;
    if (image.complete && image.naturalWidth) ready();
  };
  const observer = typeof window.IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      const state = cards.get(entry.target);
      if (state) { state.visible = entry.isIntersecting && entry.intersectionRatio > 0; paint(state); }
    });
  }, {threshold: [0, 0.01]}) : null;
  const sectionsWithin = root => [...document.querySelectorAll('[data-ll-service-rotation]')].filter(section => includes(root, section) || (sharedFor(section) && includes(root, sharedFor(section))));
  const init = (root = document) => {
    sectionsWithin(root).forEach(section => {
      let data;
      try { data = JSON.parse(section.querySelector('[data-ll-service-image-data]').textContent); } catch (_) { return; }
      const sharedScope = sharedFor(section);
      section.querySelectorAll('[data-ll-service-category]').forEach(card => {
        if (cards.has(card) || card.hasAttribute('data-ll-service-custom-image')) return;
        const image = card.querySelector('img');
        const category = card.dataset.llServiceCategory;
        const seenIds = new Set(), seenSources = new Set();
        const pool = (Array.isArray(data[category]) ? data[category] : []).filter(entry => {
          if (!entry || typeof entry.id !== 'string' || typeof entry.src !== 'string' || typeof entry.alt !== 'string' || !entry.alt.trim() || !Number.isFinite(entry.width) || !Number.isFinite(entry.height) || entry.width <= 0 || entry.height <= 0 || !['contain', 'cover'].includes(entry.fit) || typeof entry.position !== 'string') return false;
          const key = sourceKey(entry.src);
          if (!key || seenIds.has(entry.id) || seenSources.has(key)) return false;
          seenIds.add(entry.id); seenSources.add(key); return true;
        });
        if (!image || pool.length < 2) return;
        const selectionKey = section.dataset.llServiceRotation + ':' + category;
        const projects = window.LimitlessHomeProjects;
        const available = projects ? projects.servicePool(pool, card) : pool;
        const current = projects ? projects.initialService(selectionKey, available) : pool.find(entry => sourceKey(entry.src) === sourceKey(image.currentSrc || image.src));
        if (projects && !current) return;
        if (projects) {
          image.width = current.width; image.height = current.height;
          if (current.srcset) image.srcset = current.srcset; else image.removeAttribute('srcset');
          image.src = current.src;
        }
        const controller = new AbortController();
        const state = {card, section, sharedScope, image, selectionKey, pool, bag: [], failed: new Set(), controller, timer: null, callback: null, remaining: cadence, started: 0, generation: 0, pending: null, previous: null, animation: null, visible: false, focused: card.contains(document.activeElement), userPaused: sharedScope?.dataset.llSharedPaused === 'true', currentId: current?.id || null};
        cards.set(card, state);
        image.setAttribute('data-ll-service-current', '');
        if (current) {
          image.alt = current.alt;
          image.style.objectFit = current.fit;
          image.style.objectPosition = current.position;
          card.dataset.llServiceProject = current.id;
        }
        card.addEventListener('focusin', () => { state.focused = true; paint(state); }, {signal: controller.signal});
        card.addEventListener('focusout', event => { if (!card.contains(event.relatedTarget)) { state.focused = false; paint(state); } }, {signal: controller.signal});
        card.addEventListener('error', event => {
          if (event.target !== state.image || state.pending || state.previous) return;
          if (state.currentId) state.failed.add(state.currentId);
          schedule(state, () => rotate(state), 0);
        }, {capture: true, signal: controller.signal});
        if (image.complete && !image.naturalWidth && image.getAttribute('src')) {
          if (state.currentId) state.failed.add(state.currentId);
          schedule(state, () => rotate(state), 0);
        } else schedule(state, () => rotate(state), cadence);
        paint(state);
        observer?.observe(card);
      });
    });
  };
  const destroy = (root = document) => {
    cards.forEach(state => {
      if (!belongs(root, state)) return;
      stopClock(state);
      cancelPending(state);
      state.callback = null;
      state.animation?.cancel();
      state.previous?.remove();
      delete state.card.dataset.llServicePreviousProject;
      state.image.style.removeProperty('opacity');
      state.image.removeAttribute('data-ll-service-incoming');
      state.card.dataset.llServiceState = 'paused';
      state.controller.abort();
      observer?.unobserve(state.card);
      cards.delete(state.card);
    });
  };
  const setPaused = (root, paused) => {
    init(root);
    cards.forEach(state => { if (belongs(root, state)) { state.userPaused = Boolean(paused); paint(state); } });
  };
  const refresh = () => cards.forEach(paint);
  document.addEventListener('visibilitychange', refresh);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => destroy(event.target));
  window.addEventListener('pagehide', () => { pageActive = false; refresh(); });
  window.addEventListener('pageshow', () => { pageActive = true; refresh(); });
  if (motion?.addEventListener) motion.addEventListener('change', refresh); else motion?.addListener?.(refresh);
  connection?.addEventListener?.('change', refresh);
  window.LimitlessServiceRotation = {init, destroy, setPaused};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), {once: true}); else init();
})();
